import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight, Download } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, scrollToId, EASE } from "./Reveal";
import { Newsletter } from "./Newsletter";

const DS = [
  "/datasheets/electric-outboards.pdf",
  "/datasheets/e-mobility-dirt-bike-batteries.pdf",
  "/datasheets/marine-batteries.pdf",
  "/datasheets/ess-smart-home.pdf",
  "/datasheets/vtol-drone-powertrains.pdf",
];
/* display order: Marine (orig. index 2) sits one spot lower — above the most niche VTOL only */
const ORDER = [0, 1, 3, 2, 4];
export const Products = () => {
  const { t, lang } = useLang();
  const p = t.products;
  const items = ORDER.map((k) => p.items[k]);
  const punch = ORDER.map((k) => p.punch[k]);
  const [open, setOpen] = useState(0);
  const dsFolder = { pl: "pl", de: "de", fr: "fr", it: "it", hr: "hr" }[lang];
  const dsUrl = (u) => (dsFolder ? "/datasheets/" + dsFolder + u.slice("/datasheets".length) : u);

  const requestQuote = (i) => {
    window.dispatchEvent(new CustomEvent("hadron:interest", { detail: i }));
    scrollToId("contact");
  };

  return (
    <section id="products" className="relative py-24 lg:py-32 bg-ink-2/40 border-y border-line">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="mono-label">{p.eyebrow}</p>
          <h2 className="mt-4 font-display uppercase font-bold tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            {p.heading}
          </h2>
          <p className="mt-6 max-w-2xl text-txt-2 leading-relaxed">{p.lead}</p>
        </Reveal>

        <div className="mt-14 border-t border-line" data-testid="product-accordion">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b border-line">
                <button
                  data-testid={`product-trigger-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-7 text-left group"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-baseline gap-5 sm:gap-8 min-w-0">
                    <span className="font-mono2 text-xs text-txt-3 shrink-0">0{i + 1}</span>
                    <div>
                      <p className="mono-label !text-[9px]">{item.tag}</p>
                      <h3 className={`mt-1 font-display uppercase font-bold text-2xl sm:text-3xl lg:text-4xl transition-colors ${isOpen ? "text-hadron-cyan" : "text-txt group-hover:text-hadron-cyan"}`}>
                        {item.title}
                      </h3>
                    </div>
                  </div>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className={`shrink-0 h-11 w-11 grid place-items-center border ${isOpen ? "border-hadron-cyan bg-hadron-cyan text-ink" : "border-line text-txt-2 group-hover:border-hadron-cyan group-hover:text-hadron-cyan"}`}
                  >
                    <Plus size={18} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      data-testid={`product-panel-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="grid lg:grid-cols-12 gap-8 pb-10">
                        <div className="lg:col-span-5">
                          <div className="clip-frame border border-line p-2 bg-ink-2/60">
                            <img src={item.img} alt={item.title} className="w-full h-64 lg:h-80 object-cover" data-testid={`product-image-${i}`} />
                          </div>
                        </div>
                        <div className="lg:col-span-7">
                          <p className="font-display uppercase font-bold text-xl lg:text-2xl text-hadron-cyan/90" data-testid={`product-punch-${i}`}>{punch[i]}</p>
                          <p className="mt-3 text-txt-2 leading-relaxed max-w-2xl">{item.desc}</p>
                          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                            {item.f.map((f, k) => (
                              <li key={k} className="flex items-start gap-2.5 text-sm text-txt" data-testid={`product-feature-${i}-${k}`}>
                                <span className="mt-1.5 h-1.5 w-1.5 rotate-45 bg-hadron-green shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <div className="mt-7 flex flex-wrap items-center gap-2.5">
                            {item.chips.map((c) => (
                              <span key={c} className="border border-line bg-ink px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-txt-2">
                                {c}
                              </span>
                            ))}
                          </div>
                          <div className="mt-8 flex flex-wrap items-center gap-3">
                            <button
                              data-testid={`product-cta-${i}`}
                              onClick={() => requestQuote(ORDER[i])}
                              className="group inline-flex items-center gap-3 border border-hadron-cyan text-hadron-cyan px-6 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.2em] hover:bg-hadron-cyan hover:text-ink transition-colors"
                            >
                              {p.cta}
                              <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                            <a
                              data-testid={`product-datasheet-${i}`}
                              href={dsUrl(DS[ORDER[i]])}
                              download
                              className="inline-flex items-center gap-2.5 border border-line px-5 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.2em] text-txt-2 hover:text-hadron-cyan hover:border-hadron-cyan transition-colors"
                            >
                              <Download size={14} />
                              {p.datasheet}
                            </a>
                          </div>
                          <Newsletter source="products" topic={item.title} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
